import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InsightsIcon from '@mui/icons-material/Insights';
import BusinessIcon from '@mui/icons-material/Business';
import WebIcon from '@mui/icons-material/Web';
import ScrollSnapBox from '../ScrollSnapBox';
import Tech from '../../assets/images/tech.png';

const electrolysersData = [
  {
    title: 'Durable',
    description: 'Designed for long life, our electrolysers withstand harsh conditions and reduce wear, ensuring dependable performance.',
    icon: InsightsIcon,
  },
  {
    title: 'Efficient',
    description: 'Optimized for higher hydrogen production with lower energy consumption, boosting performance and reducing operational costs.',
    icon: RocketLaunchIcon,
  },
  {
    title: 'Low-Cost',
    description: 'Our streamlined design lowers both capital and operational costs, making green hydrogen production more accessible and economically viable.',
    icon: BusinessIcon,
  },
  {
    title: 'Robust',
    description: 'Engineered to perform reliably in challenging environments, our technology adapts to fluctuating inputs and varying capacities.',
    icon: WebIcon,
  },
];

// Custom styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: '#F8F9FA',
  borderRadius: 0,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[4],
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ServiceCard = ({ title, description, Icon }) => {

  return (
    <StyledCard elevation={0}>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Typography variant="h6" component="h3" fontWeight="bold" gutterBottom>
            {title}
          </Typography>
          <IconWrapper>
            <Icon sx={{ fontSize: 24, color: '#155E95' }} />
          </IconWrapper>
        </Box>
        
        <div className="text-sm md:text-base lg:text-lg text-gray-700">
          {description}
        </div>
        
      </CardContent>
    </StyledCard>
  );
};

const ElectrolysersTech = () => {
  return (
    <Box component="section" sx={{ py: 10 }}>
      <Container maxWidth="xl">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <h3 className="text-lg md:text-xl lg:text-2xl font-bold mb-2 md:mb-4 text-theme-blue">
            DISCOVER OUR TECHNOLOGY
          </h3>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Four key benefits
          </h2>
        </Box>

        <div className='mx-6 sm:mx-8 md:mx-10 lg:mx-20'>
        <Grid container spacing={3}>
          {electrolysersData.map((service, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <ServiceCard
                title={service.title}
                description={service.description}
                Icon={service.icon}
              />
            </Grid>
          ))}
        </Grid>
        </div>
      </Container>
      
      <ScrollSnapBox>
        {({ inView }) => (
          <div className="relative overflow-hidden pt-20">
            <div 
              className="transition-all duration-1000 ease-out text-center"
              style={{
                transform: `translateY(${inView ? '0' : '80px'})`,
                opacity: inView ? 1 : 0
              }}
            >
              {/* Add animation */}
              <img 
                src={Tech} 
                alt="Electrolysers Tech" 
                className="mx-auto w-3/4 lg:w-1/2"
                style={{
                  transform: `scale(${inView ? 1 : 0.9}) translateY(${inView ? '0' : '50px'})`,
                  opacity: inView ? 1 : 0,
                  transition: 'transform 1000ms ease-out, opacity 1000ms ease-out'
                }}
              />

              <p 
                className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mx-6 md:mx-12 px-4 pt-8 md:pt-12"
                style={{
                  transitionDelay: '200ms'
                }}
              >
                At Duralyze, we are paving the way for the next generation of sustainable energy with electrolyser technology
                that is not only efficient and cost-effective but also durable and robust—ensuring a greener, cleaner future for all.
              </p>
            </div>
          </div>
        )}
      </ScrollSnapBox>
    </Box>
  );
};

export default ElectrolysersTech;